<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import DeskAvatar from './DeskAvatar.vue';
import DeskBubbleTail from './DeskBubbleTail.vue';
import { useAttachmentPicker } from '../useAttachmentPicker.js';

const props = defineProps({
    body: { type: String, default: '' },
    composer: { type: Object, default: () => ({}) },
    labels: { type: Object, default: () => ({}) },
    canAddMessage: { type: Boolean, default: false },
    assistBusy: { type: Boolean, default: false },
    maxAttachmentMb: { type: Number, default: 25 },
});

const emit = defineEmits([
    'update:body', 'send-ai-draft', 'discard-draft', 'clear-draft-ui', 'toast-error',
]);

const textareaEl = ref(null);
const DRAFT_MIN_HEIGHT_PX = 80;
const DRAFT_MAX_HEIGHT_VH = 40;

const categoryConfidenceBand = computed(() => {
    const raw = props.composer?.pending_ai_draft?.confidence;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    const value = Number(raw);
    if (Number.isNaN(value)) {
        return null;
    }
    if (value >= 0.85) {
        return 'Alta';
    }
    if (value >= 0.5) {
        return 'Media';
    }

    return 'Bassa';
});

const aiDraftTitle = computed(() => (
    props.labels.aiDraftReady
    || props.labels.ai_draft_suggestion
    || 'Risposta suggerita dall\'AI'
));

const aiDraftWarning = computed(() => (
    props.labels.aiDraftWarning
    || '· Verifica sempre dettagli e tono prima dell\'invio.'
));

const hasBodyText = computed(() => Boolean(String(props.body || '').trim()));
const canSend = computed(() => props.canAddMessage && hasBodyText.value && !props.assistBusy);
const showDraftActions = computed(() => Boolean(props.composer?.can_discard || props.composer?.can_clear_ui));

const {
    fileInputEl,
    selectedFiles,
    fileKey,
    clearSelectedFiles,
    onFilesPicked,
    removeFile,
    openFilePicker,
} = useAttachmentPicker({
    maxAttachmentMb: computed(() => props.maxAttachmentMb),
    labels: computed(() => props.labels),
    onReject: (message) => emit('toast-error', message),
});

const fitDraftHeight = () => {
    const el = textareaEl.value;
    if (!el) {
        return;
    }
    el.style.height = 'auto';
    const maxPx = Math.floor((window.innerHeight || 800) * (DRAFT_MAX_HEIGHT_VH / 100));
    const next = Math.min(maxPx, Math.max(DRAFT_MIN_HEIGHT_PX, el.scrollHeight));
    el.style.height = `${next}px`;
};

const onDraftInput = (event) => {
    emit('update:body', event.target.value);
    fitDraftHeight();
};

const sendDraft = () => {
    if (!canSend.value) {
        return;
    }
    emit('send-ai-draft', {
        text: props.body,
        files: selectedFiles.value.slice(),
    });
    clearSelectedFiles();
};

watch(() => props.body, () => {
    nextTick(() => fitDraftHeight());
});

onMounted(() => {
    nextTick(() => fitDraftHeight());
});
</script>

<template>
    <article
        class="ticket-desk-message ticket-desk-message--outbound ticket-desk-message--ai-draft"
        data-testid="ticket-desk-ai-draft-bubble"
    >
        <DeskAvatar
            role="ai"
            :label="labels.aiAvatar || 'AI'"
            size="thread"
        />
        <div class="ticket-desk-message__bubble ticket-desk-ai-draft__bubble">
            <DeskBubbleTail side="out" variant="ai" />
            <div
                class="ticket-desk-ai-draft__banner"
                data-testid="ticket-desk-ai-draft-banner"
            >
                <span aria-hidden="true">✦</span>
                <strong>{{ aiDraftTitle }}</strong>
                <span>{{ aiDraftWarning }}</span>
                <span
                    v-if="categoryConfidenceBand"
                    data-testid="ticket-desk-category-confidence"
                >
                    · Confidenza categoria: {{ categoryConfidenceBand }}
                </span>
            </div>

            <textarea
                ref="textareaEl"
                class="ticket-desk-ai-draft__body"
                data-testid="ticket-desk-ai-draft-body"
                :value="body"
                :disabled="!canAddMessage || assistBusy"
                @input="onDraftInput"
            />

            <ul
                v-if="selectedFiles.length"
                class="ticket-desk-composer__attach-preview"
                data-testid="ticket-desk-ai-draft-attach-preview"
            >
                <li
                    v-for="file in selectedFiles"
                    :key="fileKey(file)"
                    class="ticket-desk-composer__attach-chip"
                >
                    <span class="ticket-desk-composer__attach-name">{{ file.name }}</span>
                    <button
                        type="button"
                        class="ticket-desk-composer__attach-remove"
                        :aria-label="labels.removeAttachment || 'Rimuovi allegato'"
                        @click="removeFile(fileKey(file))"
                    >
                        ×
                    </button>
                </li>
            </ul>

            <div class="ticket-desk-ai-draft__footer">
                <div
                    v-if="showDraftActions"
                    class="ticket-desk-ai-draft__draft-actions"
                >
                    <button
                        v-if="composer.can_discard"
                        type="button"
                        class="ticket-desk-button ticket-desk-button--ghost ticket-desk-button--sm"
                        data-testid="ticket-desk-discard-draft"
                        :disabled="assistBusy"
                        @click="emit('discard-draft')"
                    >
                        {{ labels.discardDraft || labels.discard_draft || 'Rifiuta bozza' }}
                    </button>
                    <button
                        v-if="composer.can_clear_ui"
                        type="button"
                        class="ticket-desk-button ticket-desk-button--ghost ticket-desk-button--sm"
                        data-testid="ticket-desk-hide-draft"
                        :disabled="assistBusy"
                        @click="emit('clear-draft-ui')"
                    >
                        {{ labels.clearDraftUi || labels.clear_draft || 'Nascondi bozza' }}
                    </button>
                </div>
                <div class="ticket-desk-ai-draft__send-row">
                    <input
                        ref="fileInputEl"
                        type="file"
                        class="ticket-desk-composer__file-input"
                        multiple
                        data-testid="ticket-desk-ai-draft-attach-input"
                        @change="onFilesPicked"
                    >
                    <button
                        type="button"
                        class="ticket-desk-composer__attach"
                        data-testid="ticket-desk-ai-draft-attach"
                        :disabled="assistBusy || !canAddMessage"
                        :aria-label="labels.attachFile || 'Allega file'"
                        :title="labels.attachFile || 'Allega file'"
                        @click="openFilePicker"
                    >
                        <span class="ticket-desk-icon ticket-desk-composer__attach-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" focusable="false">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                            </svg>
                        </span>
                    </button>
                    <button
                        type="button"
                        class="ticket-desk-composer__send ticket-desk-composer__send--reply ticket-desk-ai-draft__send"
                        data-testid="ticket-desk-ai-draft-send"
                        :disabled="!canSend"
                        :aria-label="labels.send || 'Invia'"
                        :title="labels.send || 'Invia'"
                        @click="sendDraft"
                    >
                        <span class="ticket-desk-icon ticket-desk-composer__send-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" focusable="false">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </article>
</template>
